import { ReportBuilderService } from '../services/report-builder.service';
import {
    IReport,
    IReportDocument,
    IReportTemplate,
    IReportTemplateArea,
    IReportTemplateRow,
    IReportTemplateRowType,
    IReportTemplateSection,
    IReportValue,
    IResponsibleUser,
    ISignature,
    ReportStatus
} from '@shared/interfaces';
import { ReportGuidanceComponent } from '../components/report-guidance/report-guidance.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NavigationService } from '../../../shared/services/navigation.service';
import { ReportLanguageSelectionComponent } from '../components/report-language-selection/report-language-selection.component';
import { GuardService, ReportService, SnackService } from '@shared/services';
import { ModuleDirective } from '../../../shared/directives/module.directive';
import { ModuleService } from '../../../shared/services/module.service';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';
import { QuestionType } from '../../../shared/enums/QuestionType';
import { ExplanationDisplayType } from '../../../shared/enums/explanation-display-type';
import { YesNoEnum } from '@shared/enums';
import { ReportSectionGuidanceComponent } from '../components/report-section-guidance/report-section-guidance.component';

export class ReportGetter extends ModuleDirective {
    protected isStandard: boolean;
    protected reportService = inject(ReportService);
    protected snackService = inject(SnackService);
    protected translateService = inject(TranslateService);

    constructor(
        public builder: ReportBuilderService,
        public dialog: MatDialog,
        public router: Router,
        public navigation: NavigationService,
        public guard: GuardService,
        moduleService: ModuleService
    ) {
        super(moduleService);
    }

    protected get report(): IReport {
        return this.builder.report;
    }

    protected set report(report: IReport) {
        this.builder.reset();
        this.builder.report = report;
    }

    protected get template(): IReportTemplate {
        return this.report ? this.report.template : null;
    }

    protected get sections() {
        return this.template ? this.template.sections : null;
    }

    protected get section(): IReportTemplateSection {
        return this.sections ? this.sections[this.builder.activeSection] : null;
    }

    protected get activeSectionIndex(): number {
        return this.builder.activeSection;
    }

    protected get areas(): IReportTemplateArea[] {
        return this.section ? this.section.areas : null;
    }

    protected get burgerOpen(): boolean {
        return this.builder.burgerOpen;
    }

    protected set burgerOpen(val: boolean) {
        this.builder.burgerOpen = val;
    }

    protected get documents(): Array<IReportDocument | File> {
        return this.report ? this.report.documents : null;
    }

    protected set documents(val: Array<IReportDocument | File>) {
        if (this.report) {
            this.report.documents = val;
        }
    }

    protected get outerStrokeColor(): string {
        if (this.report) {
            switch (this.report.reportStatus) {
                case ReportStatus.draft:
                    return '#f49629';
                case ReportStatus.inProgress:
                    return '#feff35';
                default:
                    return '#78C000';
            }
        }
        return 'white';
    }

    protected get innerStrokeColor(): string {
        return 'rgba(163,157,157,0.7)';
    }

    protected get responsible(): string[] {
        return this.report ? this.report.responsible.responsible?.map(e => e.fullName) : [];
    }

    protected get mainResponsible(): string[] {
        return this.report ? this.report.responsible.responsible?.filter(e => e.fullAccess).map(e => e.fullName) : [];
    }

    protected get specialists(): string[] {
        return this.report ? this.report.responsible.specialists?.map(e => e.fullName) : [];
    }

    protected get mainSpecialists(): string[] {
        return this.report ? this.report.responsible.specialists?.filter(e => e.fullAccess).map(e => e.fullName) : [];
    }

    protected get isAbleToEditAsResponsible(): boolean {
        return this.builder.isAbleToEditAsResponsible;
    }

    protected get isAbleToSendToCeo(): boolean {
        return this.report.doc === 0.95 && this.isAbleToSignAsSpecialist;
    }

    protected get isAbleToSignAsResponsible(): boolean {
        if (IS_TRS_PROJECT) {
            return this.builder.isAbleToSignAsResponsible && this.reportSignedBySpecialists && !this.isReleased;
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            return this.builder.isAbleToSignAsResponsible && !!this.specialistSignature && !this.isReleased;
        }
    }

    protected get isAbleToEditAsSpecialist(): boolean {
        return this.builder.isAbleToEditAsSpecialist;
    }

    protected get isAbleToSignAsSpecialist(): boolean {
        return this.builder.isAbleToSignAsSpecialist;
    }

    protected get isReleased(): boolean {
        return this.builder.isReleased;
    }

    protected get isReadyToSign(): boolean {
        return this.builder.isReadyToSign;
    }

    protected get isEditableAsResponsible(): boolean {
        const signedBySpecialist =
            this.specialistSignature instanceof Array
                ? this.specialistSignature.length > 0
                : !!this.specialistSignature;

        return this.guard.isMaster || (this.isAbleToEditAsResponsible && !signedBySpecialist && !this.isReleased);
    }

    protected get hasDocument(): boolean {
        return this.report.documents?.length > 0;
    }

    protected get language(): string {
        return this.builder.language;
    }

    protected get reportLanguage(): string {
        return this.builder.reportLanguage;
    }

    protected get specialistSignature(): ISignature | ISignature[] {
        return this.builder.specialistSignature;
    }

    get specialistSigners(): IResponsibleUser[] {
        return this.report?.responsible?.specialists?.filter(e => e.fullAccess);
    }

    get reportSignedBySpecialists(): boolean {
        const uniqueSignatureIds = new Set(
            (this.specialistSignature as ISignature[])?.map(signature => signature.id) ?? []
        ).size;

        return this.specialistSigners?.length === uniqueSignatureIds;
    }

    signatureForSigner(id: string): ISignature {
        return (this.specialistSignature as ISignature[])?.find(e => e.id === id);
    }

    protected get responsibleSignature(): ISignature {
        return this.builder.responsibleSignature;
    }

    protected get reportStartDate(): Date {
        return this.report?.reportStartDate;
    }

    protected set reportStartDate(value: Date) {
        this.report.reportStartDate = value;
    }

    protected get reportEndDate(): Date {
        return this.report?.reportEndDate;
    }

    protected set reportEndDate(value: Date) {
        this.report.reportEndDate = value;
    }

    protected get previousReport(): string {
        return this.builder.previousReport;
    }

    private get activeValues(): IReportValue[] {
        return Object.entries(this.builder.report.reportValues)
            .filter(reportValue => {
                const key = reportValue[0];
                const valueKeys =
                    this.section?.areas?.reduce((acc, cur) => {
                        for (let i = 0; i < cur.rows.length; i++) {
                            for (let j = 0; j < cur.rows[i].length; j++) {
                                if (this.isTextareaRow(cur, cur.rows[i][j])) {
                                    acc.push(cur.rows[i][j].valueId);
                                }
                            }
                        }
                        return acc;
                    }, []) || [];
                return valueKeys.includes(key);
            })
            .map(reportValue => reportValue[1]);
    }

    protected setActiveSection(index: number) {
        if (this.builder.activeSection !== index && this.report.selectedLanguage) {
            if (this.builder.activeSection !== -1 && this.builder.activeSection !== this.sections.length) {
                const activeValues = this.activeValues;
                if (activeValues.some(value => this.reportService.hasEmbeddedImages(value.value || ''))) {
                    this.snackService.openSnackBarError(
                        this.translateService.instant('report.errors.embeddedImageItem')
                    );
                    return;
                }
            }

            if (index === -1) {
                this.builder.guidanceRef.forEach(e => e.dialog.close(e.id));
                this.builder.guidanceRef = [];
            }

            if (
                index === -1 ||
                index === this.sections.length ||
                !this.sections[index].areas.some(e => !this.report.guidanceValues[e.guidanceId]) ||
                !this.builder.guidanceRef.some(e => !this.report.guidanceValues[e.id])
            ) {
                this.builder.activeSection = index;

                this.builder.guidanceRef.forEach(e => e.dialog.close(e.id));
                this.builder.guidanceRef = [];
            }

            const activeSection = this.sections[index];
            if (
                this.isAbleToEditAsSpecialist &&
                activeSection.guidance &&
                !this.report.guidanceValues[activeSection.guidance.id]
            ) {
                this.showSectionGuidance(activeSection);
            }
        }
    }

    protected isTextareaRow(question: IReportTemplateArea, row: IReportTemplateRow): boolean {
        return (
            (question.questionType === QuestionType.text && row.type === IReportTemplateRowType.textarea) ||
            (question.questionType === QuestionType.yesNo &&
                question.isExplanationDisplayed === true &&
                row.type === IReportTemplateRowType.textarea)
        );
    }

    protected radioChanged(section: IReportTemplateSection, id: string, value: string): void {
        this.report.reportValues[id].value = value;
        this.onAnswerChanged(section);
    }

    protected editorChanged(section: IReportTemplateSection): void {
        this.onAnswerChanged(section);
    }

    protected onApproveChanged(
        question: IReportTemplateArea,
        section: IReportTemplateSection,
        report: IReport,
        event: any
    ): void {
        question.rows
            .flatMap(row => row.map(item => item.valueId))
            .forEach(valueId => {
                report.reportValues[valueId].approved = event.target.checked;
            });
        this.builder.calculateDoc(section);
    }

    protected onAnswerChanged(section: IReportTemplateSection): void {
        this.builder.calculateDoc(section);
    }

    /**
     * Determines if the approval checkbox for a question should be disabled.
     *
     * This method evaluates the question type and its associated values to decide
     * whether the approval checkbox should be disabled. The behavior varies based
     * on the type of question:
     *
     * - For `text` and `value` question types, the checkbox is disabled if the primary
     *   field value is empty or undefined.
     * - For `yesNo` question types:
     *   - If the explanation is mandatory, the checkbox is disabled if either the primary
     *     field value is empty or the explanation text field value is empty.
     *   - If the explanation is not mandatory, the checkbox is disabled only if the primary
     *     field value is empty.
     * - For other question types, the checkbox is enabled by default.
     *
     * @param {IReportTemplateArea} question The question area containing rows and values.
     * @param {IReport} report The report object containing report values.
     * @returns {boolean} A boolean indicating whether the approval checkbox is disabled.
     */
    protected isApproveDisabled(question: IReportTemplateArea, report: IReport): boolean {
        const primaryField = question.rows[0][0].valueId;
        const primaryFieldValue = report.reportValues[primaryField].value;
        switch (question.questionType) {
            case QuestionType.text:
            case QuestionType.value:
                return !primaryFieldValue || primaryFieldValue.toString().length === 0;
            case QuestionType.yesNo: {
                const answerDisplayType =
                    primaryFieldValue === 'true'
                        ? ExplanationDisplayType.yes
                        : primaryFieldValue === 'false'
                          ? ExplanationDisplayType.no
                          : null;
                if (
                    question.isExplanationMandatory &&
                    (question.explanationDisplayType === ExplanationDisplayType.both ||
                        question.explanationDisplayType === answerDisplayType)
                ) {
                    const textField = question.rows[0][1].valueId;
                    const textFieldValue = report.reportValues[textField].value;
                    return (
                        !primaryFieldValue ||
                        primaryFieldValue.toString().length === 0 ||
                        !textFieldValue ||
                        textFieldValue.toString().length === 0
                    );
                } else {
                    return !primaryFieldValue || primaryFieldValue.toString().length === 0;
                }
            }
            default:
                return false;
        }
    }

    protected isApproved(question: IReportTemplateArea): boolean {
        return question.rows
            .flatMap(row => row.map(item => item.valueId))
            .every(valueId => this.report.reportValues[valueId].approved);
    }

    protected isTextQuestion(question: IReportTemplateArea): boolean {
        return question.questionType === QuestionType.text;
    }

    protected isYesNoRow(question: IReportTemplateArea, row: IReportTemplateRow): boolean {
        return question.questionType === QuestionType.yesNo && row.type === IReportTemplateRowType.checkbox;
    }

    protected isValueRow(question: IReportTemplateArea, row: IReportTemplateRow): boolean {
        return question.questionType === QuestionType.value && row.type === IReportTemplateRowType.input;
    }

    protected isExplanationDisplayed(question: IReportTemplateArea, row: IReportTemplateRow): boolean {
        return (
            question.questionType === QuestionType.yesNo &&
            row.type === IReportTemplateRowType.textarea &&
            question.isExplanationDisplayed
        );
    }

    protected areaDescription(area: IReportTemplateArea): string {
        return this.reportLanguage === 'de' && area.descriptionDe ? area.descriptionDe : area.description;
    }

    protected getItemTitle(item: IReportTemplateSection): string {
        return this.reportLanguage === 'de' && item.titleDe ? item.titleDe : item.title;
    }

    protected getItemSubtitle(item: IReportTemplateSection): string {
        return this.reportLanguage === 'de' && item.descriptionDe ? item.descriptionDe : item.description;
    }

    protected showGuidance(section: IReportTemplateSection, text: string, textDe?: string) {
        this.dialog.open(ReportGuidanceComponent, {
            position: { top: '50px' },
            width: '600px',
            panelClass: 'custom-dialog',
            closeOnNavigation: true,
            disableClose: true,
            hasBackdrop: true,
            data: {
                section,
                text,
                textDe
            }
        });
    }

    protected showSectionGuidance(section: IReportTemplateSection): void {
        if (!section.guidance) {
            this.snackService.openSnackBarError(
                this.translateService.instant('report.errors.guidanceNotFound') as string
            );
            return;
        }

        this.dialog.open(ReportSectionGuidanceComponent, {
            position: { top: '50px' },
            width: '600px',
            panelClass: 'custom-dialog',
            closeOnNavigation: true,
            disableClose: true,
            hasBackdrop: true,
            data: {
                section
            }
        });
    }

    protected isGuidanceAccepted(id: string): boolean {
        return this.report.guidanceValues[id];
    }

    protected goBack(): Promise<boolean> {
        const url = this.navigation.previousUrl === this.router.url ? null : this.navigation.previousUrl;
        return this.router.navigateByUrl(url || '/reports');
    }

    protected async save(signedBy?: string, language?: boolean, noSnack?: boolean): Promise<void> {
        /* Save handler */
    }

    protected setMode() {
        this.isStandard = this.report.reportStatus !== ReportStatus.released && this.isAbleToEditAsSpecialist;

        if (this.isStandard && !this.report.selectedLanguage) {
            if (this.isLanguageSelectionAvailable) {
                setTimeout(() => {
                    this.dialog
                        .open(ReportLanguageSelectionComponent, {
                            position: { top: '50px' },
                            width: '600px',
                            panelClass: 'custom-dialog',
                            disableClose: true,
                            closeOnNavigation: true,
                            autoFocus: false
                        })
                        .beforeClosed()
                        .subscribe(e => {
                            if (e) {
                                this.report.selectedLanguage = e;
                                this.save(null, true);
                            } else {
                                this.goBack();
                            }
                        });
                }, 50);
            } else {
                this.report.selectedLanguage = this.mainToolLanguage;
                this.save(null, true, true);
            }
        }
    }

    protected isAreaDependencySatisfied(section: IReportTemplateSection, area: IReportTemplateArea): boolean {
        if (area.dependsOn === -1) {
            return true; // No dependency, always satisfied
        }

        const dependantArea = section.areas[area.dependsOn - 1];
        const answer = this.report.reportValues[dependantArea.rows[0][0].valueId].value;
        const result = answer === 'true' ? YesNoEnum.Yes : answer === 'false' ? YesNoEnum.No : null;

        return area.dependsOnAnswer === result && this.isAreaDependencySatisfied(section, dependantArea);
    }

    /**
     * Checks if the text area should be displayed based on the answer and the area properties.
     *
     * @param {IReportTemplateArea} area The question area
     * @param {string} answer The answer to the question
     * @returns {boolean} whether the text area should be displayed
     */
    protected showTextArea(area: IReportTemplateArea, answer: string): boolean {
        const answerDisplayType = answer === 'true' ? ExplanationDisplayType.yes : ExplanationDisplayType.no;
        return (
            area.isExplanationDisplayed &&
            answer &&
            (area.explanationDisplayType === ExplanationDisplayType.both ||
                area.explanationDisplayType === answerDisplayType)
        );
    }
}
